import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
//components
import Button from "../components/Button";
import Header from "../components/Header";
import ScrollOnNavigation from "../components/ScrollOnNavigation";
import ConsentCheckboxes from "../components/ConsentCheckboxes";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
//styles
import {
  StyledAccordionHeader,
  StyledAccordionItem,
  StyledAccordionList,
  StyledAnswerContent,
  StyledContainer,
  StyledFaq,
  StyledFaqSection,
  StyledForm,
  StyledFormCard,
  StyledFormIntro,
  StyledFormSection,
  StyledFormTitle,
  StyledLinks,
  StyledLinksCard,
  StyledLinksContent,
  StyledSectionTitle,
} from "../styles/pages/faq";
//animations
import { motion } from "framer-motion";
import { PagesAnimation } from "../styles/Animations";
//languages
import { useLanguage } from "../hooks/useLanguage";
import type { LanguageProps } from "../typings";
//doc links
import {
  instagramUrl,
  instructionDorenczenia,
  instructionPodatki,
  instructionProfil,
  instructionZus1,
  instructionZus2,
  instructionZus3,
  moveChecklist,
  moveGuide,
} from "../constants/links";
//img
import { instaColor } from "../img";

type FaqItem = {
  q: string;
  a: React.ReactNode;
};

type FaqSection = {
  id: string;
  title: string;
  items: FaqItem[];
};

type AccordionItemProps = {
  item: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
};

const AccordionItem = ({ item, isOpen, onToggle }: AccordionItemProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    }
  }, []);

  return (
    <StyledAccordionItem $isOpen={isOpen}>
      <StyledAccordionHeader
        $isOpen={isOpen}
        onClick={onToggle}
        data-faq-question
      >
        <span className="question-text">{item.q}</span>
        <span className="chevron">▼</span>
      </StyledAccordionHeader>
      <div
        style={{
          height: isOpen ? height : 0,
          overflow: "hidden",
          transition: "height 0.35s ease, opacity 0.25s ease",
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div ref={contentRef} data-faq-answer>
          <StyledAnswerContent>{item.a}</StyledAnswerContent>
        </div>
      </div>
    </StyledAccordionItem>
  );
};

const Form = ({ language }: LanguageProps) => {
  const lang = useLanguage(language);
  const formSwal = withReactContent(Swal);

  //form handler
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [number, setNumber] = useState("");
  const [numberError, setNumberError] = useState("");
  const [consent, setConsent] = useState({
    data: false,
    doc: false,
  });

  useEffect(() => {
    setName("");
    setNameError("");
    setNumber("");
    setNumberError("");
  }, [language]);

  // Validation lives in one place so the same checks run both on submit and,
  // once an error is showing, live as the user keeps typing — so the message
  // disappears as soon as the field becomes valid instead of lingering until
  // the next submit.
  const validateName = (value: string): string => {
    if (value === "") {
      return lang.index.formNameErrorLength1;
    }
    if (value.length <= 1) {
      return lang.index.formNameErrorLength2;
    }
    if (
      !/^[a-zA-Zа-яА-Я, ,\-,ł,Ł,ę,Ę,ą,Ą,ó,Ó,ż,Ż,ź,Ź,ć,Ć,ś,Ś,n,Ń,å,Å,ä,Ä,ö,Ö,ü,Ü,ß,ẞ]*$/.test(
        value,
      )
    ) {
      return lang.index.formNameErrorLetters;
    }
    return "";
  };

  const validateNumber = (value: string): string => {
    if (value === "") {
      return lang.index.formNumberErrorLength1;
    }
    if (value.length <= 6) {
      return lang.index.formNumberErrorLength2;
    }
    if (!/^[+]\d/.test(value)) {
      return lang.index.formNumberErrorFormat;
    }
    return "";
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    setName(value);

    if (nameError) {
      setNameError(validateName(value));
    }
  };

  const handlePhoneNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;

    if (/^-?\d*[+-]?\d*$/.test(value)) {
      setNumber(value);

      if (numberError) {
        setNumberError(validateNumber(value));
      }
    }
  };

  const SubmitHandler = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nameValidation = validateName(name);
    const numberValidation = validateNumber(number);

    setNameError(nameValidation);
    setNumberError(numberValidation);

    if (nameValidation || numberValidation) {
      return false;
    } else {
      const data = { name: name, phone: number, site: "AgwiPol Jobs" };
      setName("");
      setNumber("");
      axios({
        method: "POST",
        url: "/api/callback",
        data: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((response) => {
          console.log("Good request: " + response);
          formSwal.fire({
            title: (
              <h2
                style={{
                  fontFamily: language === "Russian" ? "Nunito" : "Poppins",
                }}
              >
                {lang.index.formSuccessTitle}
              </h2>
            ),
            html: (
              <div
                style={{
                  fontFamily: language === "Russian" ? "Nunito" : "Poppins",
                }}
              >
                {lang.index.formSuccessText}
              </div>
            ),
            icon: "success",
            confirmButtonText: lang.index.formErrorButton,
            scrollbarPadding: false,
            backdrop: `
                rgba(0, 0, 0, 0.4)
                `,
          });
        })
        .catch((error) => {
          console.log("Bad requst: " + error);
          formSwal.fire({
            title: (
              <h2
                style={{
                  fontFamily: language === "Russian" ? "Nunito" : "Poppins",
                }}
              >
                {lang.index.formErrorTitle}
              </h2>
            ),
            html: (
              <div
                style={{
                  fontFamily: language === "Russian" ? "Nunito" : "Poppins",
                }}
              >
                {lang.index.formErrorText}
              </div>
            ),
            icon: "error",
            confirmButtonText: lang.index.formErrorButton,
            confirmButtonColor: "rgb(218, 0, 38)",
            scrollbarPadding: false,
            backdrop: `
                rgba(0, 0, 0, 0.4)
                `,
          });
        });
    }
  };

  return (
    <StyledForm onSubmit={SubmitHandler}>
      <div className="field">
        <label htmlFor="name-input">{lang.index.formName}</label>
        <input
          className={nameError.length !== 0 ? "has-error" : ""}
          name="Name"
          id="name-input"
          onChange={handleNameChange}
          placeholder={lang.index.formNamePlaceholder}
          type="text"
          value={name}
        />
        {nameError.length !== 0 && <span className="error">{nameError}</span>}
      </div>
      <div className="field">
        <label htmlFor="number-input">{lang.index.formNumber}</label>
        <input
          className={numberError.length !== 0 ? "has-error" : ""}
          name="Phone"
          id="number-input"
          onChange={handlePhoneNumberChange}
          placeholder={lang.index.formNumberPlaceholder}
          type="text"
          value={number}
        />
        {numberError.length !== 0 && (
          <span className="error">{numberError}</span>
        )}
      </div>
      <ConsentCheckboxes
        consent={consent}
        language={language}
        setConsent={setConsent}
      />
      <Button
        disabled={Object.values(consent).some((el) => el === false)}
        style={{ display: "block", margin: "1.5rem auto 0", width: "100%" }}
        size="medium"
        type="submit"
        value={lang.index.formButton}
      />
    </StyledForm>
  );
};

const Faq = ({ language }: LanguageProps) => {
  const lang = useLanguage(language);
  const f = lang.faq;
  const location = useLocation();
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const formRef = useRef<HTMLDivElement>(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Nav links point at /faq#general, /faq#residence, etc. React Router v5 does
  // not scroll to the hash on its own, so we find the matching section and bring
  // it into view. This covers two cases:
  //  - arriving from another page (router updates `location`) — the timeout lets
  //    the section list finish rendering before we measure its position;
  //  - switching sections while already on /faq, where only the hash changes —
  //    handled via the native "hashchange" event.
  useEffect(() => {
    const scrollToHashSection = () => {
      const id = window.location.hash.slice(1);
      if (!id) {
        return;
      }
      document
        .getElementById(id)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const timer = setTimeout(scrollToHashSection, 100);
    window.addEventListener("hashchange", scrollToHashSection);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("hashchange", scrollToHashSection);
    };
  }, [location]);

  const toggle = (key: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const docLinks = [
    moveGuide,
    moveChecklist,
    instructionProfil,
    instructionDorenczenia,
    instructionPodatki,
    instructionZus1,
    instructionZus2,
    instructionZus3,
  ];

  // The text lives in src/languages; here we only describe how each answer is
  // laid out (paragraphs, lists, warnings) and plug the strings back in.
  const faqData: FaqSection[] = [
    {
      id: "general",
      title: f.general.title,
      items: [
        { q: f.general.q1.q, a: <p>{f.general.q1.a}</p> },
        { q: f.general.q2.q, a: <p>{f.general.q2.a}</p> },
        {
          q: f.general.q3.q,
          a: (
            <>
              <p>{f.general.q3.intro}</p>
              <ol>
                {f.general.q3.steps.map((step, idx) => (
                  <li key={idx}>
                    <strong>{step.label}</strong> {step.text}
                  </li>
                ))}
              </ol>
            </>
          ),
        },
        {
          q: f.general.q4.q,
          a: (
            <>
              <p>{f.general.q4.p1}</p>
              <p>{f.general.q4.p2}</p>
              <div className="warning">
                <p>{f.general.q4.warning}</p>
              </div>
            </>
          ),
        },
        {
          q: f.general.q5.q,
          a: (
            <>
              <p>{f.general.q5.p1}</p>
              <p>{f.general.q5.p2}</p>
              <div className="warning">
                <p>
                  <strong>{f.general.q5.warningLabel}</strong>{" "}
                  {f.general.q5.warning1}
                </p>
                <p>{f.general.q5.warning2}</p>
              </div>
            </>
          ),
        },
      ],
    },
    {
      id: "residence",
      title: f.residence.title,
      items: [
        {
          q: f.residence.q1.q,
          a: (
            <>
              <p>{f.residence.q1.p1}</p>
              <p>{f.residence.q1.p2}</p>
            </>
          ),
        },
        {
          q: f.residence.q2.q,
          a: (
            <>
              <p>{f.residence.q2.p1}</p>
              <p>{f.residence.q2.p2}</p>
            </>
          ),
        },
        {
          q: f.residence.q3.q,
          a: (
            <>
              <p>{f.residence.q3.intro}</p>
              <ol>
                <li>
                  <strong>{f.residence.q3.step1.label}</strong>{" "}
                  {f.residence.q3.step1.text}
                </li>
                <li>
                  <strong>{f.residence.q3.step2.label}</strong>{" "}
                  {f.residence.q3.step2.text}
                </li>
                <li>
                  <strong>{f.residence.q3.step3.label}</strong>{" "}
                  {f.residence.q3.step3.text}
                </li>
                <li>
                  <strong>{f.residence.q3.step4.label}</strong>
                  <ul>
                    {f.residence.q3.step4.payments.map((p, idx) => (
                      <li key={idx}>{p}</li>
                    ))}
                  </ul>
                </li>
                <li>
                  <strong>{f.residence.q3.step5.label}</strong>{" "}
                  {f.residence.q3.step5.text}
                  <ul>
                    {f.residence.q3.step5.docs.map((d, idx) => (
                      <li key={idx}>{d}</li>
                    ))}
                  </ul>
                </li>
              </ol>
              <div className="warning">
                <p>
                  <strong>{f.residence.q3.warningLabel}</strong>{" "}
                  {f.residence.q3.warning}
                </p>
              </div>
            </>
          ),
        },
        {
          q: f.residence.q4.q,
          a: (
            <>
              <p>{f.residence.q4.intro}</p>
              <ol>
                {f.residence.q4.steps.map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ol>
              <p>{f.residence.q4.outro}</p>
            </>
          ),
        },
        {
          q: f.residence.q5.q,
          a: (
            <>
              <p>{f.residence.q5.p1}</p>
              <p>{f.residence.q5.p2}</p>
            </>
          ),
        },
        {
          q: f.residence.q6.q,
          a: (
            <>
              <p>{f.residence.q6.p1}</p>
              <p>{f.residence.q6.p2}</p>
              <div className="warning">
                <p>
                  <strong>{f.residence.q6.warningLabel}</strong>{" "}
                  {f.residence.q6.warning}
                </p>
              </div>
              <p>{f.residence.q6.p3}</p>
              <p>{f.residence.q6.p4}</p>
            </>
          ),
        },
        {
          q: f.residence.q7.q,
          a: (
            <>
              <p>{f.residence.q7.p1}</p>
              <p>{f.residence.q7.p2}</p>
              <ul>
                {f.residence.q7.risks.map((risk, idx) => (
                  <li key={idx}>{risk}</li>
                ))}
              </ul>
            </>
          ),
        },
        { q: f.residence.q8.q, a: <p>{f.residence.q8.a}</p> },
      ],
    },
    {
      id: "citizenship",
      title: f.citizenship.title,
      items: [
        {
          q: f.citizenship.q1.q,
          a: (
            <>
              <p>{f.citizenship.q1.p1}</p>
              <p>{f.citizenship.q1.p2}</p>
            </>
          ),
        },
        { q: f.citizenship.q2.q, a: <p>{f.citizenship.q2.a}</p> },
        { q: f.citizenship.q3.q, a: <p>{f.citizenship.q3.a}</p> },
        {
          q: f.citizenship.q4.q,
          a: (
            <>
              <p>{f.citizenship.q4.p1}</p>
              <p>{f.citizenship.q4.p2}</p>
              <p>{f.citizenship.q4.p3}</p>
              <p>{f.citizenship.q4.p4}</p>
            </>
          ),
        },
      ],
    },
    {
      id: "transcription",
      title: f.transcription.title,
      items: [
        { q: f.transcription.q1.q, a: <p>{f.transcription.q1.a}</p> },
        {
          q: f.transcription.q2.q,
          a: (
            <>
              <p>{f.transcription.q2.intro}</p>
              <ul>
                <li>
                  <strong>{f.transcription.q2.option1.label}</strong>{" "}
                  {f.transcription.q2.option1.text}
                </li>
                <li>
                  <strong>{f.transcription.q2.option2.label}</strong>{" "}
                  {f.transcription.q2.option2.text}
                </li>
              </ul>
            </>
          ),
        },
        {
          q: f.transcription.q3.q,
          a: (
            <>
              <p>{f.transcription.q3.p1}</p>
              <p>{f.transcription.q3.p2}</p>
            </>
          ),
        },
      ],
    },
  ];

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const questions = Array.from(
        document.querySelectorAll<HTMLElement>("[data-faq-question]"),
      );
      const answers = Array.from(
        document.querySelectorAll<HTMLElement>("[data-faq-answer]"),
      );
      const mainEntity = questions.map((question, index) => ({
        "@type": "Question",
        name: question.textContent?.trim(),
        acceptedAnswer: {
          "@type": "Answer",
          text: answers[index]?.textContent?.replace(/\s+/g, " ").trim(),
        },
      }));

      let script = document.getElementById("faq-structured-data") as
        | HTMLScriptElement
        | null;
      if (!script) {
        script = document.createElement("script");
        script.id = "faq-structured-data";
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity,
      });
    });

    return () => {
      window.clearTimeout(timer);
      document.getElementById("faq-structured-data")?.remove();
    };
  }, [language]);

  return (
    <motion.div
      variants={PagesAnimation}
      initial="hidden"
      animate="show"
      exit="exit"
    >
      <Header language={language} />
      <StyledLinks id="links">
        <StyledContainer>
          <StyledSectionTitle>{f.links.title}</StyledSectionTitle>
          <StyledLinksCard>
            <StyledLinksContent>
              <p className="intro">{f.links.intro}</p>
              <ul className="doc-list">
                {f.links.items.map((label, idx) => (
                  <li key={idx}>
                    <span className="doc-label">{label}</span>
                    <Button
                      onClick={() => {
                        if (docLinks[idx]) {
                          window.open(docLinks[idx], "_blank", "noreferrer");
                        }
                      }}
                      size="small"
                      value={f.links.downloadBtn}
                    />
                  </li>
                ))}
              </ul>
              <p className="free-note">{f.links.freeNote}</p>
              <div className="instagram">
                <div className="instagram-title">
                  <img
                    src={instaColor}
                    alt="instagram"
                    height={35}
                    width={35}
                  />
                  <p>{f.links.instagramTitle}</p>
                </div>
                <p className="instagram-text">{f.links.instagramText}</p>
                <Button
                  onClick={() =>
                    window.open(instagramUrl, "_blank", "noreferrer")
                  }
                  size="small"
                  value={f.links.instagramBtn}
                />
              </div>
            </StyledLinksContent>
          </StyledLinksCard>
        </StyledContainer>
      </StyledLinks>
      <StyledFaq>
        <StyledContainer>
          {faqData.map((section) => (
            <StyledFaqSection key={section.id} id={section.id}>
              <StyledSectionTitle>{section.title}</StyledSectionTitle>
              <StyledAccordionList>
                {section.items.map((item, idx) => {
                  const key = `${section.id}-${idx}`;
                  return (
                    <AccordionItem
                      key={key}
                      item={item}
                      isOpen={openItems.has(key)}
                      onToggle={() => toggle(key)}
                    />
                  );
                })}
              </StyledAccordionList>
              <Button
                onClick={scrollToForm}
                size="medium"
                style={{ display: "block", margin: "2rem auto 0" }}
                value={f.buttonText}
              />
            </StyledFaqSection>
          ))}
        </StyledContainer>
      </StyledFaq>
      <StyledFormSection>
        <StyledContainer ref={formRef}>
          <StyledFormTitle>{lang.index.formTitle}</StyledFormTitle>
          <StyledFormCard>
            <StyledFormIntro>
              <strong>{lang.index.formIntroTitle}</strong>
              <ul>
                {lang.index.formBenefits.map((benefit) => (
                  <li key={benefit}>{benefit}</li>
                ))}
              </ul>
            </StyledFormIntro>
            <Form language={language} />
          </StyledFormCard>
        </StyledContainer>
      </StyledFormSection>
      <ScrollOnNavigation />
    </motion.div>
  );
};

export default Faq;
